import { useMemo, useState } from 'react'
import {
  Box,
  List,
  ListItemButton,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import JsonViewer from '../../components/JsonViewer'
import { JObject } from '../../types/Constants'

interface ViewerPanelProps {
  containers: Record<string, JObject[]>
}

type ViewKey = 'fullApplication' | 'creditRevisions' | 'worksheetRevisions'

const pluralize = (name: string) => {
  if (/(s|x|z|ch|sh)$/i.test(name)) return name + 'es'
  if (/[^aeiou]y$/i.test(name)) return name.slice(0, -1) + 'ies'
  return name + 's'
}

const SINGLETON_TYPES = new Set(['FraudAnalysisResult', 'FraudFlags'])

const toCamelKey = (internalType: string) => {
  const stripped = internalType.replace(/^CreditApplication/, '') || internalType
  if (/worksheet$/i.test(stripped)) return { key: 'worksheet', isSingleton: true }
  const camel = stripped.charAt(0).toLowerCase() + stripped.slice(1)
  if (SINGLETON_TYPES.has(stripped)) return { key: camel, isSingleton: true }
  return { key: pluralize(camel), isSingleton: false }
}

const buildFullApplication = (containers: Record<string, JObject[]>): JObject => {
  const result: JObject = {}
  const creditApp = containers['db-credit/CreditApplication'] ?? []
  result.credit = creditApp[0]

  const dataDocs = containers['db-credit/CreditApplicationData'] ?? []
  const grouped: Record<string, { docs: JObject[]; isSingleton: boolean }> = {}
  for (const doc of dataDocs) {
    const type = doc._internalType as string | undefined
    if (!type) continue
    const { key, isSingleton } = toCamelKey(type)
    ;(grouped[key] ??= { docs: [], isSingleton }).docs.push(doc)
  }
  for (const key of Object.keys(grouped).sort()) {
    const { docs, isSingleton } = grouped[key]
    result[key] = isSingleton ? docs[0] : docs
  }

  const worksheetRevisions = containers['db-credit/WorksheetRevision'] ?? []
  if (worksheetRevisions.length > 0) result.worksheetRevisions = worksheetRevisions

  return result
}

const SYSTEM_FIELDS = new Set([
  '_rid',
  '_self',
  '_etag',
  '_attachments',
  '_ts',
  'id',
  'currentRevisionId',
  'birthDateAndNameSearchEntries',
  'updatedOn',
  'createdOn',
])

interface DiffRow {
  path: string
  previous: unknown
  current: unknown
}

const formatValue = (v: unknown): string => {
  if (v === undefined) return '—'
  if (v === null) return 'null'
  if (typeof v === 'string') return v
  return JSON.stringify(v)
}

const isPlainObject = (v: unknown): v is JObject =>
  v !== null && typeof v === 'object' && !Array.isArray(v)

const deepDiff = (
  older: unknown,
  newer: unknown,
  ignorePaths: Set<string>,
  path: string[] = []
): DiffRow[] => {
  if (older === newer) return []
  if (isPlainObject(older) && isPlainObject(newer)) {
    const results: DiffRow[] = []
    const keys = new Set([...Object.keys(older), ...Object.keys(newer)])
    for (const key of keys) {
      if (path.length === 0 && SYSTEM_FIELDS.has(key)) continue
      const childPath = [...path, key]
      if (ignorePaths.has(childPath.join('.'))) continue
      results.push(...deepDiff(older[key], newer[key], ignorePaths, childPath))
    }
    return results
  }
  if (JSON.stringify(older) === JSON.stringify(newer)) return []
  return [{ path: path.join('.'), previous: older, current: newer }]
}

const CREDIT_IGNORE_PATHS = new Set([
  'applicant.creditDecision',
  'decisionOverrides',
  'agentCreditDecision',
  'normsCreditDecision',
  'finalCreditDecision',
])

const WORKSHEET_IGNORE_PATHS = new Set<string>()

const sortByUpdatedAsc = (docs: JObject[]): JObject[] =>
  [...docs].sort((a, b) => {
    const ta = new Date((a.updatedOn as string) ?? 0).getTime()
    const tb = new Date((b.updatedOn as string) ?? 0).getTime()
    return ta - tb
  })

const formatTimestamp = (v: unknown): string | null =>
  v ? format(new Date(v as string), 'yyyy-MM-dd HH:mm:ss') : null

interface RevisionSubListProps {
  revisions: JObject[]
  selectedId: string | null
  onSelect: (id: string) => void
}

const RevisionSubList = ({ revisions, selectedId, onSelect }: RevisionSubListProps) => (
  <>
    {revisions.map((r) => (
      <ListItemButton
        key={r.id as string}
        selected={selectedId === r.id}
        onClick={() => onSelect(r.id as string)}
        sx={{ pl: 4 }}
      >
        <ListItemText primary={formatTimestamp(r.updatedOn) ?? (r.id as string)} />
      </ListItemButton>
    ))}
  </>
)

interface DiffPanelProps {
  revisions: JObject[]
  selectedId: string | null
  ignorePaths: Set<string>
}

const DiffPanel = ({ revisions, selectedId, ignorePaths }: DiffPanelProps) => {
  const { t } = useTranslation()

  const selectedIndex = selectedId ? revisions.findIndex((r) => r.id === selectedId) : -1
  const selectedRev = selectedIndex >= 0 ? revisions[selectedIndex] : null
  const previousRev = selectedIndex > 0 ? revisions[selectedIndex - 1] : null

  const diffRows = useMemo(() => {
    if (!selectedRev || !previousRev) return []
    return deepDiff(previousRev, selectedRev, ignorePaths)
  }, [selectedRev, previousRev, ignorePaths])

  if (!selectedRev) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {t('investigation.viewer.selectRevision')}
      </Typography>
    )
  }
  if (!previousRev) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {t('investigation.viewer.noPreviousRevision')}
      </Typography>
    )
  }
  if (diffRows.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        {t('investigation.viewer.noChanges')}
      </Typography>
    )
  }

  return (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>{t('investigation.viewer.field')}</TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {formatTimestamp(previousRev.updatedOn) ?? t('investigation.viewer.previous')}
          </TableCell>
          <TableCell sx={{ whiteSpace: 'nowrap' }}>
            {formatTimestamp(selectedRev.updatedOn) ?? t('investigation.viewer.current')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top', fontWeight: 'bold' }}>
            {t('investigation.viewer.updatedBy')}
          </TableCell>
          <TableCell sx={{ verticalAlign: 'top' }}>
            {formatValue(previousRev.updatedByUserFullname)}
          </TableCell>
          <TableCell sx={{ verticalAlign: 'top' }}>
            {formatValue(selectedRev.updatedByUserFullname)}
          </TableCell>
        </TableRow>
        {diffRows.map((row) => (
          <TableRow key={row.path}>
            <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
              {row.path}
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all', verticalAlign: 'top' }}>
              {formatValue(row.previous)}
            </TableCell>
            <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all', verticalAlign: 'top' }}>
              {formatValue(row.current)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

const ViewerPanel = ({ containers }: ViewerPanelProps) => {
  const { t } = useTranslation()
  const [view, setView] = useState<ViewKey>('fullApplication')
  const [selectedCreditRevId, setSelectedCreditRevId] = useState<string | null>(null)
  const [selectedWorksheetRevId, setSelectedWorksheetRevId] = useState<string | null>(null)

  const fullApplication = useMemo(() => buildFullApplication(containers), [containers])

  const creditRevisions = useMemo(() => {
    const revs = [...(containers['db-credit/CreditApplicationRevision'] ?? [])]
    const current = containers['db-credit/CreditApplication']?.[0]
    if (current) revs.push(current)
    return sortByUpdatedAsc(revs)
  }, [containers])

  const worksheetRevisions = useMemo(() => {
    const revs = [...(containers['db-credit/WorksheetRevision'] ?? [])]
    const dataDocs = containers['db-credit/CreditApplicationData'] ?? []
    const currentWorksheet = dataDocs.find((d) => {
      const it = d._internalType as string | undefined
      return !!it && /worksheet$/i.test(it)
    })
    if (currentWorksheet) revs.push(currentWorksheet)
    return sortByUpdatedAsc(revs)
  }, [containers])

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 250px)', gap: 2 }}>
      <Box sx={{ width: 300, borderRight: 1, borderColor: 'divider', pr: 1, overflow: 'auto' }}>
        <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
          {t('investigation.viewer.exploration')}
        </Typography>
        <List dense>
          <ListItemButton selected={view === 'fullApplication'} onClick={() => setView('fullApplication')}>
            <ListItemText primary={t('investigation.viewer.fullApplication')} />
          </ListItemButton>
          <ListItemButton selected={view === 'creditRevisions'} onClick={() => setView('creditRevisions')}>
            <ListItemText primary={t('investigation.viewer.creditRevisions')} />
          </ListItemButton>
          {view === 'creditRevisions' && (
            <RevisionSubList
              revisions={creditRevisions}
              selectedId={selectedCreditRevId}
              onSelect={setSelectedCreditRevId}
            />
          )}
          <ListItemButton
            selected={view === 'worksheetRevisions'}
            onClick={() => setView('worksheetRevisions')}
          >
            <ListItemText primary={t('investigation.viewer.worksheetRevisions')} />
          </ListItemButton>
          {view === 'worksheetRevisions' && (
            <RevisionSubList
              revisions={worksheetRevisions}
              selectedId={selectedWorksheetRevId}
              onSelect={setSelectedWorksheetRevId}
            />
          )}
        </List>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {view === 'fullApplication' && <JsonViewer documents={[fullApplication]} />}
        {view === 'creditRevisions' && (
          <DiffPanel
            revisions={creditRevisions}
            selectedId={selectedCreditRevId}
            ignorePaths={CREDIT_IGNORE_PATHS}
          />
        )}
        {view === 'worksheetRevisions' && (
          <DiffPanel
            revisions={worksheetRevisions}
            selectedId={selectedWorksheetRevId}
            ignorePaths={WORKSHEET_IGNORE_PATHS}
          />
        )}
      </Box>
    </Box>
  )
}

export default ViewerPanel
